import React, { useRef, useState } from 'react';
import { Image as ImageIcon, Upload, Trash2, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

interface ProductImage {
  path: string;
  url: string;
  isPrimary?: boolean;
  uploadedAt?: string;
}

interface Props {
  sku: string;
  images: ProductImage[];
  onChange: (images: ProductImage[]) => void;
}

/**
 * Gestione immagini articolo (Admin): upload, rimozione, imposta primaria.
 *
 * La compressione (resize/ricodifica JPEG) avviene automaticamente lato
 * backend SOLO se l'immagine eccede soglie significative (>1200px lato
 * lungo o >250KB) — vedi ``products/image_service.py``.
 */
export function AdminProductImages({ sku, images, onChange }: Props) {
  const [uploading, setUploading] = useState(false);
  const [busyPath, setBusyPath] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato non supportato. Usa JPEG, PNG o WebP.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(
        `/api/products/admin/${encodeURIComponent(sku)}/images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      onChange([...images, res.data.image]);
      toast.success('Immagine caricata con successo');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (path: string) => {
    setBusyPath(path);
    try {
      const res = await api.delete(
        `/api/products/admin/${encodeURIComponent(sku)}/images/${encodeURIComponent(path)}`,
      );
      onChange(res.data.images);
      toast.success('Immagine rimossa');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore durante la rimozione');
    } finally {
      setBusyPath(null);
    }
  };

  const handleSetPrimary = async (path: string) => {
    setBusyPath(path);
    try {
      const res = await api.put(`/api/products/admin/${encodeURIComponent(sku)}/images/primary`, { path });
      onChange(res.data.images);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore durante l\'aggiornamento');
    } finally {
      setBusyPath(null);
    }
  };

  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden" data-testid="admin-product-images">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
        <ImageIcon className="w-4 h-4 text-[hsl(214,58%,41%)]" />
        <h2 className="text-sm font-semibold tracking-tight">Immagini</h2>
        <span className="text-[10px] text-muted-foreground ml-auto">Compressione automatica se necessario</span>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
          {images.map((img) => (
            <div
              key={img.path}
              className="relative group rounded-lg border overflow-hidden bg-[#F8FAFC] aspect-square"
              data-testid={`admin-product-image-${img.path}`}
            >
              <img src={img.url} alt={sku} className="w-full h-full object-contain" />
              {img.isPrimary && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-500 text-white">
                  Primaria
                </span>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {busyPath === img.path ? (
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                ) : (
                  <>
                    {!img.isPrimary && (
                      <button
                        type="button"
                        onClick={() => handleSetPrimary(img.path)}
                        title="Imposta come primaria"
                        className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"
                        data-testid={`admin-image-set-primary-${img.path}`}
                      >
                        <Star className="w-3.5 h-3.5 text-amber-500" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(img.path)}
                      title="Rimuovi immagine"
                      className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"
                      data-testid={`admin-image-delete-${img.path}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-lg border-2 border-dashed border-secondary flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:border-[hsl(214,58%,41%)] hover:text-[hsl(214,58%,41%)] transition-colors disabled:opacity-50"
            data-testid="admin-image-upload-button"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            <span className="text-[10px] font-medium">{uploading ? 'Caricamento...' : 'Aggiungi foto'}</span>
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          data-testid="admin-image-file-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUpload(file);
          }}
        />

        {images.length === 0 && (
          <p className="text-xs text-muted-foreground">Nessuna immagine caricata per questo articolo.</p>
        )}
      </div>
    </div>
  );
}
